import {
    IonPage,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonBackButton,
    IonTitle,
    IonContent,
    IonItem,
    IonLabel,
    IonButton,
    IonIcon,
    IonGrid,
    IonRow,
    IonCol,
    IonSelect,
    IonSelectOption,
    useIonAlert,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonLoading,
    IonRadioGroup,
    IonRadio,
    IonText,
    IonModal,
    IonInput,
    IonFooter,
} from "@ionic/react";
import { useEffect, useState, useContext, useCallback } from "react";
import { useParams, useHistory } from "react-router";
import { TranslationMessagesContext, TranslationsContext } from "../util/Translations";
import { chevronBackOutline, chevronBackSharp, add, chevronDownOutline } from "ionicons/icons";
import { decodeParam, encodeParam } from "../util/ApiHelper";
import useGetWorkOrder from "../hooks/useGetWorkOrder";
import { useWorkOrderStore } from "../store/wo-order";
import { addLinkedInspection, getFilteredInspections, AddLinkedInspectionParams, FilteredInspection } from "../api/WorkOrders";

const AddInspection: React.FC = () => {
    const { workOrderId } = useParams<{ workOrderId: string }>();
    const displayWo: string = decodeParam(workOrderId);
    const history = useHistory();
    const { translatedMessages } = useContext(TranslationMessagesContext);
    const { translations } = useContext(TranslationsContext);
    const [presentAlert] = useIonAlert();

    const { trigger } = useGetWorkOrder();
    const workOrder = useWorkOrderStore((state) => state.getWorkOrderById(displayWo));

    const [filteredInspections, setFilteredInspections] = useState<FilteredInspection[]>([]);
    const [selectedInspection, setSelectedInspection] = useState<FilteredInspection | null>(null);
    const [showSelectInspectionModal, setShowSelectInspectionModal] = useState(false);
    const [selectedInspectionCodeTemp, setSelectedInspectionCodeTemp] = useState<string>("");
    const [filter, setFilter] = useState<string>("");
    const [inspectionType, setInspectionType] = useState<"PreInspection" | "PostInspection" | "Inspection">("Inspection");
    const [isRequired, setIsRequired] = useState<boolean>(false);
    const [showInitialLoading, setShowInitialLoading] = useState(false);
    const [showAddLoading, setShowAddLoading] = useState(false);
    const [searchError, setSearchError] = useState("");

    const fetchFilteredInspections = useCallback(async () => {
        if (!workOrder?.Equipment) {
            setShowInitialLoading(false);
            return;
        }
        
        setShowInitialLoading(true);
        try {
            const response = await getFilteredInspections(workOrder.Equipment);
            
            if (response.status === 200 && response.data) {
                setFilteredInspections(response?.data || []);
            } else {
                setFilteredInspections([]);
            }
        } catch (error) {
            console.error('Error fetching filtered inspections:', error);
            setFilteredInspections([]);
        } finally {
            setShowInitialLoading(false);
        }
    }, [workOrder?.Equipment]);

    const validateInputs = () => {
        setSearchError("");
        
        if (!selectedInspection) {
            setSearchError("This field is required");
            return {
                isValid: false,
                message: translatedMessages["errSelectInspection"]?.MessageText || 
                        translations["err_select_inspection"] || 
                        "Please select an inspection Id to add."
            };
        }    
        return { isValid: true, message: "" };
    };

    const filteredInspectionOptions = filteredInspections.filter((inspection) => {
        if (!filter) return true;
        const hay = `${inspection.Code} - ${inspection.Description}`.toLowerCase();
        return hay.includes(filter.toLowerCase());
    });

    const openSelectInspectionModal = () => {
        setSelectedInspectionCodeTemp(selectedInspection?.Code ?? "");
        setFilter("");
        setShowSelectInspectionModal(true);
    };

    const closeSelectInspectionModal = () => {
        setShowSelectInspectionModal(false);
        setFilter("");
    };

    const confirmSelectInspection = () => {
        if (!selectedInspectionCodeTemp) {
            setSearchError("This field is required");
            return;
        }
        const selected = filteredInspections.find(ins => ins.Code === selectedInspectionCodeTemp);
        if (selected) {
            handleInspectionSelect(selected);
        }
        setShowSelectInspectionModal(false);
        setFilter("");
    };

    const handleAddInspection = async () => {
        const validation = validateInputs();
        if (!validation.isValid) {
            presentAlert({
                header: translations["lbl_error"] || "Error",
                message: validation.message,
                buttons: [translations["lbl_ok"] || "OK"]
            });
            return;
        }
        
        setShowAddLoading(true);
        
        try {
            const parseWorkOrderComponents = (workOrderNumber: string) => {
                const parts = workOrderNumber.split('-').filter(part => part.trim() !== '');
                
                return {
                    base: parts[0] || '',          
                    task: parts[1] || '  ',        
                    subtask: parts[2] || '  '       
                };
            };

            const { base, task, subtask } = parseWorkOrderComponents(displayWo);

            const requestData: AddLinkedInspectionParams = {
                InspectionId: selectedInspection?.Code.toString() || '',
                WoBase: base,
                WoTask: task,
                WoSubtask: subtask,
                PostInspection: inspectionType === "PostInspection",
                PreInspection: inspectionType === "PreInspection",
                Required: isRequired,
            };

            const response = await addLinkedInspection(requestData);

            if (response.status === 200 && response.data?.Success === true) {
                setSelectedInspection(null);
                setInspectionType("Inspection");
                setIsRequired(false);
                
                presentAlert({
                    header: translations["lbl_success"] || "Success",
                    message: translatedMessages["msgInspectionAdded"]?.MessageText || translations["msg_inspection_added"] || "Inspection added successfully!",
                    buttons: [
                        {
                            text: translations["lbl_ok"] || "OK",
                            handler: () => {
                                history.replace(`/workorder/${encodeParam(displayWo)}/inspections`);
                            }
                        }
                    ]
                });
            } else {
                const messageList = response.data?.MessageList;
                let errorMessage = "";
                let isDuplicateError = false;
                
                if (messageList && Array.isArray(messageList) && messageList.length > 0) {
                    const firstMessage = messageList[0];
                    errorMessage = firstMessage.Text || "";
                    if (firstMessage.Id === "WoInspectionLinkExists" || 
                        errorMessage.toLowerCase().includes("already exists") ||
                        errorMessage.toLowerCase().includes("inspection link")) {
                        isDuplicateError = true;
                    }
                }
                
                if (isDuplicateError) {
                    presentAlert({
                        header: translations["lbl_error"] || "Error",
                        message: "This work order inspection link already exists.",
                        buttons: [translations["lbl_ok"] || "OK"]
                    });
                } else {
                    const displayMessage = errorMessage || 
                                          translatedMessages["errAddInspection"]?.MessageText || 
                                          translations["err_add_inspection"] || 
                                          "Failed to add inspection. Please try again.";
                    
                    presentAlert({
                        header: translations["lbl_error"] || "Error",
                        message: displayMessage,
                        buttons: [translations["lbl_ok"] || "OK"]
                    });
                }
                
                setSelectedInspection(null);
            }
        } catch (error) {
            console.error("Error adding inspection:", error); 
            setSelectedInspection(null);
            presentAlert({
                header: translations["lbl_error"] || "Error",
                message: translatedMessages["errGeneralError"]?.MessageText || translations["err_general_error"] || "An error occurred while adding the inspection.",
                buttons: [translations["lbl_ok"] || "OK"]
            });
        } finally {
            setShowAddLoading(false);
        }
    };

    const handleInspectionSelect = (inspection: FilteredInspection) => {
        setSelectedInspection(inspection);
        setSearchError("");
    };

    useEffect(() => {
        if (displayWo && !workOrder) {
            trigger(displayWo);
        }
    }, [displayWo, workOrder, trigger]);

    useEffect(() => {
        if (workOrder?.Equipment) {
            fetchFilteredInspections();
        }
    }, [workOrder?.Equipment, fetchFilteredInspections]);

    useEffect(() => {
        setSelectedInspection(null);
        setInspectionType("Inspection");
        setIsRequired(false);
        setSearchError("");
    }, [displayWo]);

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonBackButton
                            defaultHref={`/workorder/${encodeParam(displayWo)}/inspections`}
                            icon={{ ios: chevronBackOutline, md: chevronBackSharp }}
                            text=""
                        />
                    </IonButtons>
                    <IonTitle>
                        {translations["lbl_add_inspection"] || "Add Inspection"}
                    </IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding">
                <IonCard>
                    <IonCardHeader>
                    </IonCardHeader>
                    <IonCardContent>
                        <IonGrid>
                            <IonRow>
                                <IonCol size="12">
                                    <IonItem className={searchError ? "ion-invalid" : ""} lines="none">
                                        <IonLabel position="stacked">
                                            {translations["lbl_select_inspection"] || "Select Inspection"}
                                            <span style={{ color: "red" }}> *</span>
                                        </IonLabel>
                                        <IonItem
                                            class="dropdown-item"
                                            lines="none"
                                            onClick={openSelectInspectionModal}
                                            style={{
                                                width: "100%",
                                                border: "1px solid var(--ion-color-step-150, #c8c7cc)",
                                                borderRadius: 4,
                                                marginTop: 6,
                                            }}
                                        >
                                            <IonLabel
                                                slot="start"
                                                className="align-value"
                                                style={{
                                                    position: "absolute",
                                                    transform: "translateY(-40%)",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    marginTop: 10,
                                                }}
                                            >
                                                {selectedInspection?.Code || ""}
                                            </IonLabel>
                                            <IonIcon
                                                icon={chevronDownOutline}
                                                className="dropdown-icon"
                                                slot="end"
                                                style={{
                                                    position: "absolute",
                                                    right: "0%",
                                                    transform: "translateY(-30%)",
                                                }}
                                            />
                                        </IonItem>
                                        {searchError && (
                                            <IonText slot="helper" color="danger">
                                                {searchError}
                                            </IonText>
                                        )}
                                    </IonItem>
                                    <IonModal isOpen={showSelectInspectionModal} backdropDismiss={false}>
                                        <IonHeader>
                                            <IonToolbar>
                                                <IonTitle>{translations["lbl_select_inspection"] || "Select Inspection"}</IonTitle>
                                                <IonButtons slot="end">
                                                    <IonButton onClick={closeSelectInspectionModal}>Close</IonButton>
                                                </IonButtons>
                                            </IonToolbar>
                                        </IonHeader>
                                        <IonContent>
                                            <IonItem>
                                                <IonInput
                                                    placeholder={translations["lbl_filter_Inspection"]|| "Filter Inspection"}
                                                    value={filter}
                                                    onIonChange={(e) => setFilter(String(e.detail.value || ""))}
                                                ></IonInput>
                                            </IonItem>
                                            <IonRadioGroup
                                                value={selectedInspectionCodeTemp}
                                                onIonChange={(e) => setSelectedInspectionCodeTemp(e.detail.value)}
                                            >
                                                {filteredInspectionOptions.map((inspection, index) => (
                                                    <IonItem key={`${inspection.InspectionId}-${index}`}>
                                                        <IonLabel>
                                                            {`${inspection.Code} - ${inspection.Description}`}
                                                        </IonLabel>
                                                        <IonRadio slot="start" value={inspection.Code} />
                                                    </IonItem>
                                                ))}
                                            </IonRadioGroup>
                                        </IonContent>
                                        <IonFooter>
                                            <div style={{ padding: 12 }}>
                                                <IonButton expand="block" onClick={confirmSelectInspection}>
                                                    {translations["lbl_ok"] || "OK"}
                                                </IonButton>
                                                <IonButton style={{ marginTop: 8 }} fill="outline" expand="block" onClick={closeSelectInspectionModal}>
                                                    {translations["lbl_btn_cancel"] || translations["lbl_cancel"] || "Cancel"}
                                                </IonButton>
                                            </div>
                                        </IonFooter>
                                    </IonModal>
                                </IonCol>
                            </IonRow>
                            
                            <IonRow>
                                <IonCol size="12">
                                    <IonItem>
                                        <IonLabel>
                                            <IonText>
                                                {translations["lbl_inspection_type"] || "Inspection Type"}
                                            </IonText>
                                        </IonLabel>
                                    </IonItem>
                                    <IonRadioGroup 
                                        value={inspectionType} 
                                        onIonChange={(e) => {
                                            setInspectionType(e.detail.value);
                                        }}
                                    >
                                        <IonItem>
                                            <IonRadio slot="start" value="Inspection" className="ion-radio" />
                                            <IonLabel>
                                                {translations["lbl_inspection"] || "Inspection"}
                                            </IonLabel>
                                        </IonItem>
                                        <IonItem>
                                            <IonRadio slot="start" value="PreInspection" className="ion-radio" />
                                            <IonLabel>
                                                {translations["lbl_pre_job_inspection"] || "Pre Job Inspection"}
                                            </IonLabel>
                                        </IonItem>
                                        <IonItem>
                                            <IonRadio slot="start" value="PostInspection" className="ion-radio" />
                                            <IonLabel>
                                                {translations["lbl_post_job_inspection"] || "Post Job Inspection"}
                                            </IonLabel>
                                        </IonItem>
                                    </IonRadioGroup>
                                </IonCol>
                            </IonRow>
                            
                            <IonRow>
                                <IonCol size="12">
                                    <IonItem>
                                        <IonLabel position="stacked">
                                            {translations["lbl_required"] || "Required"}
                                        </IonLabel>
                                        <IonSelect
                                            value={isRequired}
                                            placeholder={translations["lbl_select_required"] || "Select Required"}
                                            onIonChange={(e) => setIsRequired(e.detail.value)}
                                        >
                                            <IonSelectOption value={false}>
                                                {translations["lbl_no"] || "No"}
                                            </IonSelectOption>
                                            <IonSelectOption value={true}>
                                                {translations["lbl_yes"] || "Yes"}
                                            </IonSelectOption>
                                        </IonSelect>
                                    </IonItem>
                                </IonCol>
                            </IonRow>
                            
                            <IonRow>
                                <IonCol size="12">
                                    <IonButton
                                        expand="block"
                                        onClick={handleAddInspection}
                                        disabled={showAddLoading} 
                                        color="primary"
                                    >
                                        <IonIcon icon={add} slot="start" />
                                        {translations["lbl_btn_add"] || "Add"}
                                    </IonButton>
                                </IonCol>
                            </IonRow>
                        </IonGrid>
                    </IonCardContent>
                </IonCard>

                <IonLoading
                    cssClass="my-custom-class"
                    isOpen={showInitialLoading}
                    onDidDismiss={() => setShowInitialLoading(false)}
                />

                <IonLoading
                    cssClass="my-custom-class"
                    isOpen={showAddLoading}
                    onDidDismiss={() => setShowAddLoading(false)}
                />
            </IonContent>
        </IonPage>
    );
};

export default AddInspection;